/**
 * Formatting in the house style (docs/52 §7.3): numbers as digits, dates as "Thursday, Oct 9", times as
 * "2:14 pm" in the business's time zone, lists as "A, B and C" (no Oxford comma), money as "$497".
 *
 * These never throw: the voice lint calls every exported function with sample text, and a bad value on a
 * screen should show as itself, not crash the screen.
 */

/** "1 photo", "3 photos". */
export const n = (count: number, one: string, many: string): string => `${count} ${count === 1 ? one : many}`;

/** "A", "A and B", "A, B and C". Never an Oxford comma. */
export function list(items: readonly string[]): string {
  if (!Array.isArray(items)) return String(items);
  const xs = items.filter((x) => typeof x === 'string' && x.length > 0);
  if (xs.length <= 1) return xs[0] ?? '';
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

function valid(d: unknown): d is Date {
  return d instanceof Date && !Number.isNaN(d.getTime());
}

function toDate(v: Date | string | number): Date | null {
  const d = v instanceof Date ? v : new Date(v);
  return valid(d) ? d : null;
}

function fmt(v: Date | string | number, zone: string, opts: Intl.DateTimeFormatOptions): string | null {
  const d = toDate(v);
  if (!d) return null;
  try {
    return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: zone }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'America/New_York' }).format(d);
  }
}

/** "Thursday, Oct 9". */
export function day(v: Date | string | number, zone = 'America/New_York'): string {
  return fmt(v, zone, { weekday: 'long', month: 'short', day: 'numeric' }) ?? String(v);
}

/** "Oct 9". */
export function shortDate(v: Date | string | number, zone = 'America/New_York'): string {
  return fmt(v, zone, { month: 'short', day: 'numeric' }) ?? String(v);
}

/** "Aug 3, 2027": dates more than a few months away carry the year. */
export function longDate(v: Date | string | number, zone = 'America/New_York'): string {
  return fmt(v, zone, { month: 'short', day: 'numeric', year: 'numeric' }) ?? String(v);
}

/** "2:14 pm". */
export function time(v: Date | string | number, zone = 'America/New_York'): string {
  const s = fmt(v, zone, { hour: 'numeric', minute: '2-digit', hour12: true });
  return s ? s.replace(/\s?AM$/, ' am').replace(/\s?PM$/, ' pm').replace(':00 ', ' ') : String(v);
}

/** "$497", "$5,467", "$12.50". Amounts are in cents, as Stripe gives them. */
export function money(cents: number): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return String(cents);
  const dollars = cents / 100;
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2, maximumFractionDigits: 2 })}`;
}

/** "October". */
export function month(v: Date | string | number, zone = 'America/New_York'): string {
  return fmt(v, zone, { month: 'long' }) ?? String(v);
}

/** "d•••@riveraplumbing.com". */
export function maskEmail(email: string): string {
  const at = typeof email === 'string' ? email.indexOf('@') : -1;
  if (at < 1) return String(email);
  return `${email[0]}•••${email.slice(at)}`;
}
